import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnInit,
  Output,
  signal,
  WritableSignal
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { evaluateFormula, getFormulaRelevantIds } from '../../../../utils/formulaTransformers/parseFormulas';
import { combineLatest, map, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseItemProperties } from './base-item-properties';
import { UserInformation } from '@lfw/roesberg.liveforms.core';
import { EditorSettings } from '../../types/editor-settings';
import { xmlElementToString } from '../../../../utils/xmlParser';

// Explicit interface for BaseItemComponent public API - for better IDE support
export interface IBaseItem<T> {
  xml: InputSignal<ChildNode>;
  form: InputSignal<FormGroup>;
  user: InputSignal<UserInformation>;
  settings: InputSignal<EditorSettings>;
  sectionItemId: InputSignal<string>;
  firstSiblingItemId: InputSignal<string>;
  renderedXmlItems: InputSignal<ChildNode[]>;
  renderedXmlItemsQty: InputSignal<number>;
  isReadonly: InputSignal<boolean>;
  properties: BaseItemProperties & T;
  sharedFirstColumnWidths: WritableSignal<Record<string, number>>;
  isVisible: WritableSignal<boolean>;
  isGenericRepeatItem: WritableSignal<boolean>;
  visibilityChanged: EventEmitter<boolean>;
  htmlId: () => string;
  currentId: () => string;
  getXML: () => string;
  extractLocalProperties: (xml: Element) => BaseItemProperties & T;
}

@Component({
  template: '',
  standalone: false
})
export abstract class BaseItemComponent<T> implements OnInit {
  xml = input.required<ChildNode>();
  form = input.required<FormGroup>();
  user = input.required<UserInformation>();
  settings = input.required<EditorSettings>();
  sectionItemId = input.required<string>();
  firstSiblingItemId = input.required<string>();
  renderedXmlItems = input.required<ChildNode[]>();
  renderedXmlItemsQty = input.required<number>();
  isReadonly = input.required<boolean>();

  abstract properties: BaseItemProperties & T;

  sharedFirstColumnWidths = signal<Record<string, number>>({});
  public isVisible = signal(true);
  isGenericRepeatItem = signal(false);
  @Output() visibilityChanged = new EventEmitter<boolean>();

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.properties = this.extractLocalProperties(this.xml() as Element);
    this.isGenericRepeatItem.set(this.properties.isGenericRepeatItem);
    this.isVisible.set(!this.isGenericRepeatItem());
    if (this.properties.isGenericRepeatItem) {
      this.visibilityChanged.emit(this.isVisible());
    }
    this.setupActivationRule();
  }

  getXML() {
    return xmlElementToString(this.xml()) || '';
  }

  abstract extractLocalProperties(xml: Element): BaseItemProperties & T;

  abstract htmlId(): string;

  abstract currentId(): string;

  private setupActivationRule(): void {
    if (!this.properties.activationRule) {
      return;
    }

    const ids = getFormulaRelevantIds(this.properties.activationRule);

    const ctrls = ids.map((id) => this.form().get(id)).filter((c): c is AbstractControl => !!c);

    if (ctrls.length === 0) {
      return;
    }

    const streams = ctrls.map((c, index) =>
      c.valueChanges.pipe(
        startWith(c.value),
        map((value) => ({ id: ids[index], control: c, value }))
      )
    );

    combineLatest(streams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((controlData) => {
        const result = evaluateFormula(this.properties.activationRule, controlData);
        this.onActivationRuleEvaluated(result);
      });
  }

  private onActivationRuleEvaluated(result: any) {
    const newVisibility = !!result;
    const currentVisibility = this.isVisible();
    const effectiveVisibility = !this.isGenericRepeatItem() && newVisibility;

    if (effectiveVisibility !== currentVisibility) {
      this.isVisible.set(effectiveVisibility);
      this.visibilityChanged.emit(effectiveVisibility);
    }
  }
}
